import {memo} from 'react';

import styled from 'styled-components';
import {isUndefined} from 'lodash';

import {SkeletonLoader, Skeleton} from '~/shared/components/Loaders';
import {flipOnLTR} from '~/shared/theme/utils';
import {getLocalizationService} from '~/shared/services/localisationService';

import {Title, TotalRow} from '../shared';

import Price from './Price';
import FilterSelection from './FilterSelection';
import {Root, TableMobileWrapper, Column as CustomColumn, InnerRow, DateAndTime, Arrow} from './styled';

const CustomArrow = styled(Arrow).attrs(({theme: {isLTR}}) => ({
  forward: isLTR,
}))`
  display: block;
  height: 15px;
  width: 10px;
  ${flipOnLTR`
    right: auto;
  `}
`;

const ColumnWithBorder = styled(CustomColumn)`
  border-bottom: 2px solid ${({theme}) => theme.colors.darkBackground};
  padding-top: 16px;
  padding-bottom: 16px;
  ${flipOnLTR`
    padding-left: 41px;
  `}

  &:nth-last-child(2) {
    border-bottom: 0;
  }
`;

const LoaderColumnWithBorder = styled(ColumnWithBorder)`
  ${flipOnLTR`
    padding-left: 12px;
  `}
`;

const SkeletonMobileInnerRow = styled(InnerRow)`
  margin-bottom: 10px;
`;

const StyledInnerRow = styled(InnerRow)`
  &:first-child {
    margin-bottom: 8px;
  }
`;

const LoaderComponent = () => {
  return (
    <>
      <LoaderColumnWithBorder key={1} narrow noPaddingLeft>
        <SkeletonMobileInnerRow>
          <div>
            <Skeleton width={42} height={10} />
          </div>
          <div>
            <Skeleton width={61} height={10} />
          </div>
        </SkeletonMobileInnerRow>
        <SkeletonMobileInnerRow>
          <DateAndTime>
            <Skeleton width={61} height={10} />
          </DateAndTime>
          <div>
            <Skeleton width={42} height={10} />
          </div>
        </SkeletonMobileInnerRow>
      </LoaderColumnWithBorder>

      <LoaderColumnWithBorder key={2} narrow noPaddingLeft>
        <SkeletonMobileInnerRow>
          <div>
            <Skeleton width={42} height={10} />
          </div>
          <div>
            <Skeleton width={61} height={10} />
          </div>
        </SkeletonMobileInnerRow>
        <SkeletonMobileInnerRow>
          <DateAndTime>
            <Skeleton width={61} height={10} />
          </DateAndTime>
          <div>
            <Skeleton width={42} height={10} />
          </div>
        </SkeletonMobileInnerRow>
      </LoaderColumnWithBorder>

      <LoaderColumnWithBorder key={3} narrow noPaddingLeft>
        <SkeletonMobileInnerRow>
          <div>
            <Skeleton width={42} height={10} />
          </div>
          <div>
            <Skeleton width={61} height={10} />
          </div>
        </SkeletonMobileInnerRow>
        <SkeletonMobileInnerRow>
          <DateAndTime>
            <Skeleton width={61} height={10} />
          </DateAndTime>
          <div>
            <Skeleton width={42} height={10} />
          </div>
        </SkeletonMobileInnerRow>
      </LoaderColumnWithBorder>
    </>
  );
};

const MobileTransactionTable = ({
  allPayments,
  onFilterChange,
  visibleItems,
  checkIfMoneycardIsCredit,
  onMobileTransactionClick,
  hideTitle,
  hideFilters,
  customTableFooterColor,
}) => {
  const {t} = getLocalizationService();

  return (
    <Root>
      {!hideTitle && <Title isMobile>{t('transaction_report')}</Title>}
      {!hideFilters && (
        <FilterSelection
          placeholder={t('all_payment_options')}
          filterOptions={allPayments}
          onFilterChange={onFilterChange}
          top={-4}
          left={0}
          checkIfMoneycardIsCredit={checkIfMoneycardIsCredit}
        />
      )}
      <TableMobileWrapper>
        <SkeletonLoader shouldShowLoader={isUndefined(visibleItems)} LoaderComponent={LoaderComponent}>
          {visibleItems?.map((item, index) => {
            const {restaurantName, total, dateAndTime, orderType, marked, isCanceled} = item;
            if (marked) {
              const label = restaurantName || t('total_charges');
              return <TotalRow key={`${total}_${restaurantName}`} label={label} price={total} t={t} customTableFooterColor={customTableFooterColor} />;
            }

            return (
              <ColumnWithBorder
                // eslint-disable-next-line react/no-array-index-key
                key={`mobile_${index}_${dateAndTime}_${total}`}
                onClick={() => onMobileTransactionClick(item)}
                data-test-id={`order_id_${item.id}`}
                narrow
              >
                <StyledInnerRow bold>
                  <div data-test-id="cell_restaurantName">{restaurantName}</div>
                  <Price isBold dataTestId="cell_total" price={total} isCanceled={isCanceled} t={t} />
                </StyledInnerRow>
                <StyledInnerRow>
                  <DateAndTime data-test-id="cell_dateAndTime">{dateAndTime}</DateAndTime>
                  <div data-test-id="cell_orderType">{orderType}</div>
                </StyledInnerRow>
                <CustomArrow noAlt />
              </ColumnWithBorder>
            );
          })}
        </SkeletonLoader>
      </TableMobileWrapper>
    </Root>
  );
};

export default memo(MobileTransactionTable);
